<script>
import LineChart from '@components/charts/line-chart.vue';
import { breakpointsMixin } from '@mixins/breakpointsMixin';
import { authComputed, orgComputed, orgMethods } from '@state/helpers';

export default {
  components: {
    LineChart,
  },
  mixins: [breakpointsMixin],
  props: {
    goals: {
      type: Object,
      required: true,
      default: () => {},
    },
    lineChartHeight: {
      type: Number,
      required: true,
      default: 200,
    },
  },
  data() {
    return {
      goalData: {
        labels: this.months(),
        datasets: [
          {
            label: false,
            backgroundColor: 'rgb(64 89 225 / 0.5)',
            data: this.goalValues(),
          },
        ],
      },
      chartOpts: {
        tooltips: {
          enabled: false,
        },
        scales: {
          yAxes: [
            {
              ticks: {
                display: true,
                beginAtZero: false,
                stepSize: 10,
                suggestedMin: 150,
                fontColor: 'white',
                max: 250,
                min: 150,
              },
              gridLines: {
                display: false,
                fontColor: 'white',
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                display: true,
                color: 'rgb(255 255 255 / 0.2)',
              },
              ticks: {
                fontColor: 'white',
                fontSize: 18,
              },
            },
          ],
        },
        legend: {
          display: false,
          labels: {
            fontColor: 'white',
            fontSize: 18,
          },
        },
        responsive: true,
        maintainAspectRatio: false,
      },
    };
  },
  beforeMount() {
    let min = 150;
    let max = 250;
    if (this.userProgress.goals.weight.value < this.userProgress.initial.weight.value) {
      min = this.userProgress.goals.weight.value;
      max = this.userProgress.initial.weight.value;
    }
    if (this.userProgress.goals.weight.value > this.userProgress.initial.weight.value) {
      min = this.userProgress.initial.weight.value;
      max = this.userProgress.goals.weight.value;
    }
    this.chartOpts.scales.yAxes[0].ticks.min = parseInt(min);
    this.chartOpts.scales.yAxes[0].ticks.max = parseInt(max);
  },
  computed: {
    ...authComputed,
    ...orgComputed,
    colors() {
      return {
        '--color': this.primaryColor,
      }
    },
    lineChartStyles() {
      return {
        height: `${this.lineChartHeight}px`,
        position: 'relative',
      };
    },
    currentGoalValue() {
      return this.goals.data.map((data) => data.month);
    },
  },
  mounted() {},
  methods: {
    ...orgMethods,
    months() {
      return this.goals.data.map((data) => data.month);
    },
    goalValues() {
      return this.goals.data.map((data) => data.current.value);
    },
  },
};
</script>

<template>
  <div :class="['card-goals mobile', { isMobile: !$_isDesktopView() }]">
    <b-row>
      <b-col md="12" sm="12">
        <div class="goals-header">
          <h2>Goals</h2>
          <div v-if="!$_isDesktopView()" class="subtitle mobile">weight</div>
        </div>
        <div v-if="$_isDesktopView()" class="subtitle">weight</div>
      </b-col>
    </b-row>

    <div class="row">
      <div class="col-8 col-md-9">
        <div class="line-chart-wrapper">
          <LineChart :data="goalData" :options="chartOpts" :styles="lineChartStyles"> </LineChart>
        </div>
      </div>
      <div class="col-4 col-md-2 offset-md-1">
        <h2 class="weight-current">
          {{ userProgress.initial.weight.value }} <sup>lb</sup>
          <small :style="colors">{{ userProgress.goals.weight.value }}</small>
        </h2>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.card-goals {
  background: $color-black-alt;
  border-radius: 11.5474px;
  height: 50vh;
  max-height: 40rem;
  min-height: 30rem;
  padding: 2.7rem 2.2rem;

  @include media('xl', null) {
    box-shadow: $color-card-base-box-shadow;
    padding: 4.7rem 4rem;
  }
  &.isMobile {
    height: 26vh;
    max-width: 60rem;
  }
  .goals-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 1.8rem;

    h2 {
      color: white;
      margin-bottom: 0;
    }

    .icon-arrow {
      color: white;
      font-size: 1.7rem;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .subtitle,
  .weight-current,
  .weight-goal {
    font-family: $base-font-family;
    font-style: normal;
    font-weight: normal;
  }

  .subtitle {
    @extend %typography-medium;

    color: #888;
    font-weight: bold;
    letter-spacing: 1.21922px;
    line-height: 2.4rem;
    margin-top: 0.5rem;
    text-transform: uppercase;

    // XL Tablet up
    @include media('xl', null) {
      font-size: 17.3529px;
      margin-bottom: 2.5rem;
    }

    &.mobile {
      left: 10rem;
      margin-bottom: 0;
      position: absolute;
    }
  }

  .weight-current {
    @include calc-font-size(2);

    color: white;
    letter-spacing: 0.271592px;
    line-height: 4.9rem;
    padding-left: 1rem;

    // Large Mobile up
    @include media('lg', null) {
      @include calc-font-size(2.6);
    }

    // XL Tablet up
    @include media('xl', null) {
      @include calc-font-size(4);

      padding-left: 0;
    }

    sup {
      color: white;
      letter-spacing: 0.271592px;
      line-height: 4.9rem;
      margin-left: 0.8rem;

      // XL Tablet up
      @include media('xl', null) {
        @include calc-font-size(3);

        margin-left: 0;
      }
    }

    small {
      @include calc-font-size(1.8);

      color: var(--color);
      display: block;
      font-family: $secondary-font-family;
      font-style: normal;
      font-weight: 600;
      letter-spacing: 0.271592px;
      line-height: 3.6rem;
      position: relative;
      top: -2.2rem;

      // Large Mobile up
      @include media('lg', null) {
        @include calc-font-size(2.2);

        top: -1.5rem;
      }

      // XL Tablet up
      @include media('xl', null) {
        padding-left: 0.5rem;
        top: 0;
      }
    }
  }

  .line-chart-wrapper {
    padding-bottom: 2rem;
    position: relative;
    width: 60vw;
  }
}
</style>
